import "./multiple-column-list.scss";
import { ReactNode } from "react";
import cn from "clsx";
import {
  CtaLink,
  CtaLinkProps,
} from "design-system/components/primitives/cta-link/cta-link";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import { Alignment, EditAttributes } from "design-system/types/types";

export interface MultipleColumnListProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  align?: Alignment;
  items: Array<CtaLinkProps>;
  media?: MediaAssetProps;

  editAttributes?: {
    title?: EditAttributes;
    subtitle?: EditAttributes;
  };
}

/**
 * For displaying a list of subitems in a column format
 *
 *
 * - **`id: CB-012-000-00`**
 * - **`data-region: cb__multiple-column-list`**
 */
export function MultipleColumnList({
  title,
  subtitle,
  align = "center",
  items,
  media,
  editAttributes,
}: MultipleColumnListProps) {
  const multipleColumnListClass = cn(
    "hbs-multiple-column-list",
    media
      ? "hbs-multiple-column-list--has-media"
      : "hbs-multiple-column-list--no-media",
  );

  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component--multiple-column-list",
  );

  const listClass = cn(
    "hbs-multiple-column-list__list",
    items.length > 3 && "hbs-multiple-column-list__list--long",
  );

  return (
    <div className={wrapperClass} data-region="cb__multiple-column-list">
      <div className={multipleColumnListClass}>
        <div className="hbs-multiple-column-list__header">
          {title && (
            <h2
              className="hbs-multiple-column-list__title hbs-text-h3"
              {...editAttributes?.title}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p
              className="hbs-multiple-column-list__subtitle"
              {...editAttributes?.subtitle}
            >
              {subtitle}
            </p>
          )}
        </div>
        {items && items.length > 0 && (
          <div className="hbs-multiple-column-list__items">
            <ul className={listClass}>
              {items.map((item, index) => (
                <li className="hbs-multiple-column-list__item" key={index}>
                  <CtaLink
                    {...item}
                    className="hbs-multiple-column-list__link"
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        {media && (
          <div className="hbs-multiple-column-list__media">
            <MediaAsset {...media} aspectRatio="3/2" />
          </div>
        )}
      </div>
    </div>
  );
}
